import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTransaction, getSettings, getSubCategories,
  updateTransaction, createTransaction, getRelatedTransactions,
  addCategory, addSubCategory, addTag,
  setSplits, revertSplit, getBusinesses,
} from '../api'
import { useBusinessContext } from '../context/BusinessContext'
import { useMode } from '../context/ModeContext'
import { PBBadge, CatPill } from '../components/txDisplay'

interface FormState {
  file: string
  date: string
  description: string
  merchantDisplayName: string
  charges: string
  categoryId: string
  subCategoryId: string
  tagIds: number[]
}

const EMPTY: FormState = {
  file: '', date: '', description: '', merchantDisplayName: '',
  charges: '', categoryId: '', subCategoryId: '', tagIds: [],
}

type RelatedTx = {
  id: string; date: string; merchantDisplayName: string; description: string
  category: string | null; subCategory: string | null; tags: string; amount: number | null
  hasCat: boolean; hasTags: boolean; pbLabel: 'P' | 'B' | 'PB'
  isSplit?: boolean
  primaryLineBusinessId?: number | null
  splitLines?: { category: string | null; subCategory: string | null; tags?: string[]; amount: number; businessId?: number | null }[]
}

interface SplitRow {
  key: number
  amount: string
  businessId: number | null
  categoryId: string
  subCategoryId: string
  note: string
  tagIds: number[]
}

let _splitKey = 0
const nextSplitKey = () => ++_splitKey
const blankRow = (): SplitRow => ({ key: nextSplitKey(), amount: '', businessId: null, categoryId: '', subCategoryId: '', note: '', tagIds: [] })

function money(v: number | null | undefined) {
  if (v == null || isNaN(v)) return '--'
  const abs = Math.abs(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (v < 0 ? '-' : '') + '$' + abs
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.08em', color: 'var(--pb-muted)',
  marginBottom: '0.35rem', display: 'block',
}
const INPUT: React.CSSProperties = {
  width: '100%', padding: '0.75rem 0.9rem',
  borderRadius: 12, border: '1px solid var(--pb-glass-border)',
  background: 'var(--pb-glass-control-bg)', color: 'var(--pb-text)', fontSize: '1rem',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  outline: 'none',
}
const SELECT: React.CSSProperties = {
  ...INPUT,
  paddingRight: '2.75rem',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
}

/* ── Inline quick-add panel (shown below dropdown/tags) ────────────────────── */
function InlineAdd({
  placeholder, onConfirm, onCancel, loading, icon = 'bi-tags',
}: {
  placeholder: string
  onConfirm: (name: string) => void
  onCancel: () => void
  loading?: boolean
  icon?: string
}) {
  const [val, setVal] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return (
    <div style={{
      display: 'flex', gap: '0.4rem', marginTop: '0.45rem', alignItems: 'center',
      padding: '0.45rem 0.65rem', background: '#eef2ff',
      borderRadius: 10, border: '1.5px solid #c7d2fe',
    }}>
      <i className={`bi ${icon}`} style={{ color: '#6366f1', flexShrink: 0, fontSize: '0.85rem' }} />
      <input
        ref={ref}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && val.trim()) onConfirm(val.trim())
          if (e.key === 'Escape') onCancel()
        }}
        placeholder={placeholder}
        style={{
          flex: 1, border: 'none', background: 'transparent',
          fontSize: '0.9rem', color: 'var(--pb-text)', outline: 'none',
          fontFamily: 'inherit',
        }}
      />
      <button type="button" disabled={!val.trim() || loading}
        onClick={() => val.trim() && onConfirm(val.trim())}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: 7, border: 'none',
          background: '#6366f1', color: '#fff', cursor: val.trim() && !loading ? 'pointer' : 'not-allowed',
          opacity: val.trim() && !loading ? 1 : 0.6, fontSize: '0.85rem', transition: 'background 0.12s',
        }} title="Save">
        {loading ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <i className="bi bi-check2" />}
      </button>
      <button type="button" onClick={onCancel} style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 7, border: 'none',
        background: 'transparent', color: 'var(--pb-muted)', cursor: 'pointer', fontSize: '1rem',
        transition: 'background 0.12s',
      }} title="Cancel">
        <i className="bi bi-x" />
      </button>
    </div>
  )
}

// ── BusinessToggle ────────────────────────────────────────────────────────────
function BusinessToggle({ businessId, businesses, onChange }: {
  businessId: number | null
  businesses: { businessID: number; businessName: string }[]
  onChange: (id: number | null) => void
}) {
  const isBusiness = businessId !== null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      {/* P / B segmented toggle */}
      <div style={{ display: 'inline-flex', borderRadius: 8, border: '1px solid var(--pb-border)', overflow: 'hidden', flexShrink: 0 }}>
        <button type="button" onClick={() => onChange(null)} style={{
          padding: '0.25rem 0.65rem', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
          background: !isBusiness ? '#6366f1' : 'transparent',
          color: !isBusiness ? '#fff' : 'var(--pb-muted)',
          transition: 'background 0.15s, color 0.15s',
        }}>Personal</button>
        <button type="button" onClick={() => onChange(businesses[0]?.businessID ?? -1)} style={{
          padding: '0.25rem 0.65rem', border: 'none', borderLeft: '1px solid var(--pb-border)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
          background: isBusiness ? '#6366f1' : 'transparent',
          color: isBusiness ? '#fff' : 'var(--pb-muted)',
          transition: 'background 0.15s, color 0.15s',
        }}>Business</button>
      </div>
      {/* Business selector — shown only when Business is active */}
      {isBusiness && businesses.length > 0 && (
        <select
          value={businessId ?? ''}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            fontSize: '0.8rem', padding: '0.25rem 0.55rem', borderRadius: 7,
            border: '1px solid var(--pb-border)', background: 'var(--pb-card-bg,#fff)',
            color: 'var(--pb-text)', cursor: 'pointer', maxWidth: 180,
          }}
        >
          {businesses.map(b => <option key={b.businessID} value={b.businessID}>{b.businessName}</option>)}
        </select>
      )}
    </div>
  )
}

export default function TransactionEdit() {
  const { id } = useParams<{ id?: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { mode } = useMode()
  const { selectedBusinessId } = useBusinessContext()
  const bizId = mode === 'business' ? selectedBusinessId : null

  // When opened as "Add transaction" from the Accounts page, these carry the target
  // account (and its business) so the new transaction is tied to that account.
  const [searchParams] = useSearchParams()
  const newAccountId   = isNew ? searchParams.get('account')     : null
  const newAccountName = isNew ? searchParams.get('accountName') : null
  const newBusinessParam = isNew ? searchParams.get('business')  : null
  const newBusinessId  = newBusinessParam ? parseInt(newBusinessParam, 10) : null

  const [form, setForm]                   = useState<FormState>(EMPTY)
  const [applyToOthers, setApplyToOthers] = useState(false)
  const [applyMatchBy, setApplyMatchBy]   = useState<'merchant' | 'description'>('merchant')
  const [applyCategory, setApplyCategory] = useState(false)
  const [applyCategoryMode, setApplyCategoryMode] = useState<'all' | 'unknownOnly'>('all')
  const [applyTags, setApplyTags]         = useState(false)
  const [applyTagsMode, setApplyTagsMode] = useState<'all' | 'noTagsOnly'>('all')
  const [applyMerchant, setApplyMerchant] = useState(false)
  const [primaryLineBusinessId, setPrimaryLineBusinessId] = useState<number | null>(null)
  const [applyBusiness, setApplyBusiness] = useState(false)
  const [applyBusinessId, setApplyBusinessId] = useState<number | null>(null)
  const [error, setError]                 = useState<string | null>(null)
  const [showModal, setShowModal]         = useState(false)
  const [modalFilter, setModalFilter]     = useState<'all' | 'unknownOnly' | 'noTagsOnly' | 'effective'>('all')

  // Quick-add state
  const [showAddCat, setShowAddCat]       = useState(false)
  const [showAddSub, setShowAddSub]       = useState(false)
  const [showAddTag, setShowAddTag]       = useState(false)
  const [splitEnabled, setSplitEnabled]   = useState(false)
  const [splitRows, setSplitRows]         = useState<SplitRow[]>([blankRow()])
  const [splitAddCatKey, setSplitAddCatKey] = useState<number | null>(null)
  const [splitAddSubKey, setSplitAddSubKey] = useState<number | null>(null)
  const [splitAddTagKey, setSplitAddTagKey] = useState<number | null>(null)

  const { data: tx, isLoading: txLoading } = useQuery({
    queryKey: ['transaction', id],
    queryFn:  () => getTransaction(id!),
    enabled:  !isNew && !!id,
  })

  const settingsKey = ['settings', bizId ?? null]
  const { data: settings } = useQuery({
    queryKey: settingsKey,
    queryFn:  () => getSettings(bizId ?? undefined),
    staleTime: 5 * 60_000,
  })

  const { data: businesses = [] } = useQuery({
    queryKey: ['businesses'],
    queryFn:  getBusinesses,
    staleTime: 300_000,
  })

  const catId = parseInt(form.categoryId)
  const { data: subCats } = useQuery({
    queryKey: ['subcategories', catId],
    queryFn:  () => getSubCategories(catId),
    enabled:  !!catId,
  })

  // Keep the bulk business assignment locked to the line's business while the toggle is on.
  useEffect(() => {
    if (applyBusiness) setApplyBusinessId(primaryLineBusinessId)
  }, [applyBusiness, primaryLineBusinessId])

  const relatedValue = applyMatchBy === 'merchant' ? (tx?.merchantDisplayName ?? '') : (tx?.description ?? '')
  const { data: related, isFetching: relatedFetching } = useQuery({
    queryKey: ['related', applyMatchBy, relatedValue, id],
    queryFn:  () => getRelatedTransactions(applyMatchBy, relatedValue, id!),
    enabled:  applyToOthers && !isNew && !!tx && !!id,
  })

  useEffect(() => {
    if (!tx) return
    setForm({
      file:                tx.file                ?? '',
      date:                tx.date                ?? '',
      description:         tx.description         ?? '',
      merchantDisplayName: tx.merchantDisplayName ?? '',
      charges:             (tx.isSplit && tx.splits?.length >= 2)
                             ? String(tx.splits[0].amount)
                             : (tx.charges?.toString() ?? ''),
      categoryId:    tx.categoryId?.toString()    ?? '',
      subCategoryId: tx.subCategoryId?.toString() ?? '',
      tagIds:        tx.tags.map((t: { id: number }) => t.id),
    })
    // Always restore primary-line business from the dedicated field
    setPrimaryLineBusinessId(tx.primaryLineBusinessId ?? null)

    if (tx.isSplit && tx.splits && tx.splits.length >= 2) {
      setSplitEnabled(true)
      // splits[0] is the original portion (read-only panel); splits[1..] are editable
      setSplitRows(tx.splits.slice(1).map((s: { splitId: number; amount: number; businessId: number | null; categoryId: number | null; subCategoryId: number | null; note: string | null; tags: { id: number }[] }) => ({
        key: nextSplitKey(),
        amount: String(s.amount),
        businessId: s.businessId ?? null,
        categoryId: String(s.categoryId ?? ''),
        subCategoryId: String(s.subCategoryId ?? ''),
        note: s.note ?? '',
        tagIds: s.tags.map((t: { id: number }) => t.id),
      })))
    } else {
      setSplitEnabled(false)
      setSplitRows([blankRow()])
    }
  }, [tx])

  // New-mode defaults: today's date + the target account's business (from the Accounts page).
  useEffect(() => {
    if (!isNew) return
    setForm(f => ({ ...f, date: f.date || new Date().toISOString().slice(0, 10) }))
    if (newBusinessId != null) setPrimaryLineBusinessId(newBusinessId)
  }, [isNew, newBusinessId])

  const saveMutation = useMutation({
    mutationFn: async (): Promise<{ id: string } | void> => {
      const payload = {
        file:                form.file                || null,
        date:                form.date                || null,
        description:         form.description         || null,
        merchantDisplayName: form.merchantDisplayName || null,
        charges:             splitEnabled
                               ? (tx?.charges ?? null)
                               : (form.charges ? parseFloat(form.charges) : null),
        categoryId:    form.categoryId    ? parseInt(form.categoryId)      : null,
        subCategoryId: form.subCategoryId ? parseInt(form.subCategoryId)  : null,
        tagIds:        form.tagIds,
      }
      if (isNew) return createTransaction({
        ...payload,
        file:           'Manual Entry',
        merchant:       form.merchantDisplayName || null,   // mirror display name into raw merchant
        plaidAccountId: newAccountId,
        businessId:     primaryLineBusinessId,
      })
      return updateTransaction(id!, {
        id: id!,
        ...payload,
        primaryLineBusinessId,
        applyToOthers,
        applyMatchBy,
        applyCategory,
        applyCategoryMode,
        applyTags,
        applyTagsMode,
        applyMerchant,
        applyBusiness,
        applyBusinessId,
      })
    },
    onSuccess: async (result) => {
      const txId = isNew ? (result as { id: string }).id : id!
      try {
        const dbTotal     = Math.abs(tx?.charges || 0)
        const originalAmt = Math.abs(parseFloat(form.charges) || 0)

        if (splitEnabled) {
          const editSum = splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
          const money = (n: number) => Math.abs(n).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })

          // The full set of lines = the primary/original portion (top CHARGES line,
          // included only when it has a non-zero amount) + the carved split rows.
          // The backend requires ≥2 lines that sum exactly to the transaction total.
          const lines = [
            ...(originalAmt > 0.005 ? [{
              amount:        originalAmt,
              businessId:    primaryLineBusinessId,
              categoryId:    form.categoryId    ? Number(form.categoryId)    : null,
              subCategoryId: form.subCategoryId ? Number(form.subCategoryId) : null,
              note:          null as string | null,
              tagIds:        form.tagIds,
            }] : []),
            ...splitRows.map(r => ({
              amount:        parseFloat(r.amount) || 0,
              businessId:    r.businessId,
              categoryId:    r.categoryId    ? Number(r.categoryId)    : null,
              subCategoryId: r.subCategoryId ? Number(r.subCategoryId) : null,
              note:          r.note || null,
              tagIds:        r.tagIds,
            })),
          ]

          if (lines.length < 2) {
            setError('A split needs at least 2 allocations — keep some on the original amount, or add another split.')
            return
          }
          const sum = originalAmt + editSum
          if (Math.abs(sum - dbTotal) > 0.01) {
            setError(`Allocations (${money(sum)}) must equal the transaction total (${money(dbTotal)}). Remaining: ${money(dbTotal - sum)}.`)
            return
          }
          await setSplits(txId, lines)
        } else if (!splitEnabled && tx?.isSplit) {
          await revertSplit(txId)
        }
      } catch (e) {
        setError((e as Error).message)
        return
      }
      // Invalidate every list the edit can affect. Each page's table reads from its
      // own key (some with a 5-min staleTime), so all must be marked stale or the row
      // shows pre-edit data on return.
      ;['transactions-all', 'dashboard', 'dash-table', 'biz-transactions', 'month', 'categories', 'merchants', 'tags']
        .forEach(k => qc.invalidateQueries({ queryKey: [k] }))
      qc.invalidateQueries({ queryKey: ['transaction', id] })
      navigate(-1)
    },
    onError: (e: Error) => setError(e.message),
  })

  /* ── Quick-add mutations ────────────────────────────────────────────────── */
  const addCatMutation = useMutation({
    mutationFn: (name: string) => addCategory(name, bizId ?? undefined),
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: settingsKey })
      setField('categoryId', (cat as { id: number }).id.toString())
      setShowAddCat(false)
    },
    onError: (e: Error) => setError(e.message),
  })

  const addSubMutation = useMutation({
    mutationFn: (name: string) => addSubCategory(catId, name),
    onSuccess: (sub) => {
      qc.invalidateQueries({ queryKey: ['subcategories', catId] })
      setField('subCategoryId', (sub as { id: number }).id.toString())
      setShowAddSub(false)
    },
    onError: (e: Error) => setError(e.message),
  })

  const addTagMutation = useMutation({
    mutationFn: (name: string) => addTag(name, bizId ?? undefined),
    onSuccess: (tag) => {
      qc.invalidateQueries({ queryKey: settingsKey })
      toggleTag((tag as { id: number }).id)
      setShowAddTag(false)
    },
    onError: (e: Error) => setError(e.message),
  })

  // ── Split-row quick-add mutations ─────────────────────────────────────────
  // Variables carry rowKey so onSuccess always targets the right row,
  // avoiding stale-closure issues with splitAddCatKey state.
  const addSplitCatMutation = useMutation({
    mutationFn: ({ name }: { name: string; rowKey: number }) => addCategory(name, bizId ?? undefined),
    onSuccess: (cat, { rowKey }) => {
      qc.invalidateQueries({ queryKey: settingsKey })
      setSplitRows(prev => prev.map(r =>
        r.key === rowKey ? { ...r, categoryId: String((cat as { id: number }).id), subCategoryId: '' } : r
      ))
      setSplitAddCatKey(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const addSplitSubMutation = useMutation({
    mutationFn: ({ name, rowCatId }: { name: string; rowCatId: number; rowKey: number }) =>
      addSubCategory(rowCatId, name),
    onSuccess: (sub, { rowCatId, rowKey }) => {
      qc.invalidateQueries({ queryKey: ['subcategories', rowCatId] })
      setSplitRows(prev => prev.map(r =>
        r.key === rowKey ? { ...r, subCategoryId: String((sub as { id: number }).id) } : r
      ))
      setSplitAddSubKey(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const addSplitTagMutation = useMutation({
    mutationFn: ({ name }: { name: string; rowKey: number }) => addTag(name, bizId ?? undefined),
    onSuccess: (tag, { rowKey }) => {
      qc.invalidateQueries({ queryKey: settingsKey })
      const newId = (tag as { id: number }).id
      setSplitRows(prev => prev.map(r =>
        r.key === rowKey ? { ...r, tagIds: [...r.tagIds, newId] } : r
      ))
      setSplitAddTagKey(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    if (key === 'categoryId') {
      setForm(f => ({ ...f, categoryId: value as string, subCategoryId: '' }))
      setShowAddSub(false)
    } else {
      setForm(f => ({ ...f, [key]: value }))
    }
  }

  function toggleTag(tagId: number) {
    setForm(f => ({
      ...f,
      tagIds: f.tagIds.includes(tagId)
        ? f.tagIds.filter(t => t !== tagId)
        : [...f.tagIds, tagId],
    }))
  }

  const sameMerchantCount    = tx?.sameMerchantCount    ?? 0
  const sameDescriptionCount = tx?.sameDescriptionCount ?? 0
  const hasBulkApply         = !isNew && (sameMerchantCount > 0 || sameDescriptionCount > 0)

  const relatedList: RelatedTx[] = (related as RelatedTx[] | undefined) ?? []
  const total  = relatedList.length
  const noCat  = relatedList.filter(t => !t.hasCat).length
  const noTags = relatedList.filter(t => !t.hasTags).length

  /* ── Effective affected rows based on current checked options ───────────── */
  const effectiveRows = (() => {
    if (!applyCategory && !applyTags && !applyMerchant && !applyBusiness) return []
    const set = new Set<RelatedTx>()
    if (applyCategory) {
      const rows = applyCategoryMode === 'unknownOnly' ? relatedList.filter(t => !t.hasCat) : relatedList
      rows.forEach(r => set.add(r))
    }
    if (applyTags) {
      const rows = applyTagsMode === 'noTagsOnly' ? relatedList.filter(t => !t.hasTags) : relatedList
      rows.forEach(r => set.add(r))
    }
    if (applyMerchant)  relatedList.forEach(r => set.add(r))
    if (applyBusiness)  relatedList.forEach(r => set.add(r))
    return [...set]
  })()
  const effectiveCount = effectiveRows.length

  function openModal(filter: 'all' | 'unknownOnly' | 'noTagsOnly' | 'effective') {
    setModalFilter(filter)
    setShowModal(true)
  }

  const filteredRows =
    modalFilter === 'unknownOnly' ? relatedList.filter(t => !t.hasCat) :
    modalFilter === 'noTagsOnly'  ? relatedList.filter(t => !t.hasTags) :
    modalFilter === 'effective'   ? effectiveRows :
    relatedList

  const filterLabel =
    modalFilter === 'unknownOnly' ? ' - unknown category only' :
    modalFilter === 'noTagsOnly'  ? ' - untagged only' :
    modalFilter === 'effective'   ? ' - affected transactions' : ''

  /* ── Focus handlers ─────────────────────────────────────────────────────── */
  const focusOn  = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.currentTarget.style.borderColor = '#a5b4fc'
    e.currentTarget.style.boxShadow   = '0 0 0 4px rgba(99,102,241,0.15)'
    e.currentTarget.style.background  = 'var(--pb-glass-bg-strong)'
  }
  const focusOff = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.currentTarget.style.borderColor = 'var(--pb-glass-border)'
    e.currentTarget.style.boxShadow   = 'none'
    e.currentTarget.style.background  = 'var(--pb-glass-control-bg)'
  }

  if (!isNew && txLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <span className="spinner-border text-primary me-2" />
        <span style={{ color: 'var(--pb-muted)' }}>Loading transaction...</span>
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger mb-3" style={{ borderRadius: 10 }}>
          <i className="bi bi-exclamation-triangle me-2" />{error}
          <button className="btn-close float-end" onClick={() => setError(null)} />
        </div>
      )}

      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{
          background: 'var(--pb-card-bg)',
          backdropFilter: 'blur(12px) saturate(160%)',
          WebkitBackdropFilter: 'blur(12px) saturate(160%)',
          border: '1px solid var(--pb-glass-border)',
          borderRadius: 20,
          padding: '1.75rem 1.75rem 1.5rem',
          boxShadow: 'var(--pb-glass-shadow), var(--pb-glass-inset)',
        }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.4rem' }}>
            <span style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
              background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 50%,#db2777 100%)',
              boxShadow: '0 10px 24px -10px rgba(99,102,241,0.55)',
              fontSize: '1.2rem',
            }}>
              <i className={`bi bi-${isNew ? 'plus-lg' : 'receipt'}`} />
            </span>
            <div>
              <h2 style={{ fontWeight: 800, letterSpacing: '-0.02em', margin: 0, fontSize: '1.25rem' }}>
                {isNew ? 'Add Transaction' : (tx?.description || tx?.merchantDisplayName || 'Edit Transaction')}
              </h2>
              {!isNew && tx?.merchant && (
                <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', marginTop: '0.15rem', fontWeight: 500 }}>
                  {tx.merchant}
                </div>
              )}
              {isNew && newAccountName && (
                <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', marginTop: '0.15rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  <i className="bi bi-credit-card-2-front" />Account: <strong style={{ color: 'var(--pb-text)' }}>{newAccountName}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Top chips */}
          {!isNew && tx && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.4rem', marginTop: '0.8rem' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                fontSize: '0.72rem', color: 'var(--pb-muted)',
                background: 'var(--pb-glass-control-bg)', padding: '0.2rem 0.6rem',
                borderRadius: 8, border: '1px solid var(--pb-border)',
              }}>
                <i className="bi bi-hash" />
                <span style={{ fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize: '0.68rem' }}>{tx.id}</span>
              </div>
              {tx.account && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  fontSize: '0.72rem', fontWeight: 600,
                  background: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe',
                  padding: '0.2rem 0.6rem', borderRadius: 8,
                }}>
                  <i className="bi bi-credit-card" /> {tx.account}
                </div>
              )}
              {/* Split badge — shown when transaction is split */}
              {tx.isSplit && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  fontSize: '0.72rem', fontWeight: 700,
                  background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)',
                  border: '1px solid rgba(99,102,241,0.3)',
                  padding: '0.2rem 0.6rem', borderRadius: 8,
                }}>
                  <i className="bi bi-scissors" style={{ fontSize: '0.68rem' }} /> Split
                </div>
              )}
            </div>
          )}

          {/* Row 1: Merchant | Date */}
          <div className="row g-3">
            <div className="col-sm-6">
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={FIELD_LABEL}>Display Name</label>
                <input style={INPUT} placeholder="e.g. Starbucks" value={form.merchantDisplayName}
                  onChange={e => setField('merchantDisplayName', e.target.value)} onFocus={focusOn} onBlur={focusOff} />
              </div>
            </div>
            <div className="col-sm-6">
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={FIELD_LABEL}>Date</label>
                <input style={INPUT} type="date" value={form.date}
                  onChange={e => setField('date', e.target.value)} onFocus={focusOn} onBlur={focusOff} />
              </div>
            </div>
          </div>

          {/* Row 2: Charges | Category | SubCategory */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '1.1rem' }}>
            <div style={{ flex: '0 0 160px' }}>
              <label style={FIELD_LABEL}>Charges</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: '0.95rem', top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--pb-muted)', fontWeight: 600, pointerEvents: 'none',
                }}>$</span>
                <input style={{ ...INPUT, paddingLeft: '1.85rem' }} type="number" step="0.01" placeholder="0.00"
                  value={form.charges} onChange={e => setField('charges', e.target.value)} onFocus={focusOn} onBlur={focusOff} />
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', gap: '1rem', flexWrap: 'wrap', minWidth: 0 }}>

              {/* Category */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={FIELD_LABEL}>Category</label>
                <div style={{ position: 'relative' }}>
                  <select style={SELECT} value={form.categoryId}
                    onChange={e => {
                      if (e.target.value === '__new__') {
                        e.target.value = form.categoryId
                        setShowAddCat(true)
                      } else {
                        setField('categoryId', e.target.value)
                      }
                    }} onFocus={focusOn} onBlur={focusOff}>
                    <option value="">— Select category —</option>
                    {settings?.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="__new__" style={{ color: '#6366f1', fontWeight: 600 }}>＋ Add new category…</option>
                  </select>
                  <i className="bi bi-chevron-down" style={{
                    position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)',
                    pointerEvents: 'none', fontSize: '0.75rem', color: 'var(--pb-muted)',
                  }} />
                </div>
                {showAddCat && (
                  <InlineAdd
                    placeholder="New category name…"
                    icon="bi-tags"
                    loading={addCatMutation.isPending}
                    onConfirm={name => addCatMutation.mutate(name)}
                    onCancel={() => setShowAddCat(false)}
                  />
                )}
              </div>

              {/* Subcategory */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={FIELD_LABEL}>Subcategory</label>
                <div style={{ position: 'relative' }}>
                  <select style={{ ...SELECT, cursor: form.categoryId ? 'pointer' : 'not-allowed', opacity: form.categoryId ? 1 : 0.5 }}
                    value={form.subCategoryId}
                    onChange={e => {
                      if (e.target.value === '__new__') {
                        e.target.value = form.subCategoryId
                        setShowAddSub(true)
                      } else {
                        setField('subCategoryId', e.target.value)
                      }
                    }}
                    disabled={!form.categoryId} onFocus={focusOn} onBlur={focusOff}>
                    <option value="">— Select subcategory —</option>
                    {subCats?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    {form.categoryId && <option value="__new__" style={{ color: '#6366f1', fontWeight: 600 }}>＋ Add new subcategory…</option>}
                  </select>
                  <i className="bi bi-chevron-down" style={{
                    position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)',
                    pointerEvents: 'none', fontSize: '0.75rem', color: 'var(--pb-muted)',
                    opacity: form.categoryId ? 1 : 0.4,
                  }} />
                </div>
                {showAddSub && (
                  <InlineAdd
                    placeholder="New subcategory name…"
                    icon="bi-tag"
                    loading={addSubMutation.isPending}
                    onConfirm={name => addSubMutation.mutate(name)}
                    onCancel={() => setShowAddSub(false)}
                  />
                )}
              </div>

              {/* Scissors — split toggle, aligned to bottom of fields */}
              {!isNew && (form.charges !== '' && parseFloat(form.charges) !== 0) && (
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', paddingBottom: '0px' }}>
                  <button
                    type="button"
                    title={splitEnabled ? 'Disable split' : 'Split transaction'}
                    onClick={() => {
                      if (!splitEnabled) {
                        // Original shown as read-only panel; just one blank editable row for the split
                        setSplitRows([blankRow()])
                      }
                      setSplitEnabled(v => !v)
                    }}
                    style={{
                      width: 44, height: 46,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 12,
                      border: splitEnabled ? '1.5px solid rgba(99,102,241,0.45)' : '1px solid var(--pb-glass-border)',
                      background: splitEnabled ? 'rgba(99,102,241,0.12)' : 'var(--pb-glass-control-bg)',
                      color: splitEnabled ? 'var(--pb-primary)' : 'var(--pb-muted)',
                      cursor: 'pointer', fontSize: '1rem', transition: 'all 0.15s',
                    }}>
                    <i className="bi bi-scissors" />
                  </button>
                </div>
              )}

            </div>
          </div>

          {/* Add mode extra fields. File/Source are auto-set to "Manual Entry" on save. */}
          {isNew && (
            <div className="row g-3">
              <div className="col-12">
                <div style={{ marginBottom: '1.1rem' }}>
                  <label style={FIELD_LABEL}>Description</label>
                  <input style={INPUT} placeholder="Original transaction description" value={form.description}
                    onChange={e => setField('description', e.target.value)} onFocus={focusOn} onBlur={focusOff} />
                </div>
              </div>
            </div>
          )}

          {/* Tags */}
          <div style={{ marginTop: '1.25rem', paddingTop: '0.6rem', borderTop: '1px dashed var(--pb-border)' }}>
            <label style={FIELD_LABEL}>Tags</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.35rem', alignItems: 'flex-start' }}>
              {settings?.tags.map(tag => {
                const sel = form.tagIds.includes(tag.id)
                return (
                  <button key={tag.id} type="button" onClick={() => toggleTag(tag.id)} style={{
                    padding: '0.28rem 0.65rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
                    border: sel ? '1.5px solid transparent' : '1.5px solid var(--pb-border)',
                    background: sel ? (tag.color ?? '#4f46e5') : 'transparent',
                    color: sel ? '#fff' : 'var(--pb-muted)', cursor: 'pointer', transition: 'all 0.12s',
                  }}>
                    {tag.name}
                  </button>
                )
              })}
              {/* Add new tag pill */}
              {!showAddTag && (
                <button type="button" onClick={() => setShowAddTag(true)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.28rem 0.65rem', borderRadius: 999,
                  fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                  border: '1.5px dashed #a5b4fc', background: 'transparent', color: '#6366f1',
                  transition: 'background 0.12s, border-color 0.12s',
                }}>
                  <i className="bi bi-plus" style={{ fontSize: '0.9rem' }} /> Add new tag
                </button>
              )}
              {!settings?.tags?.length && !showAddTag && (
                <span style={{ fontSize: '0.82rem', color: 'var(--pb-muted)' }}>No tags defined yet</span>
              )}
            </div>
            {showAddTag && (
              <InlineAdd
                placeholder="New tag name…"
                icon="bi-tag"
                loading={addTagMutation.isPending}
                onConfirm={name => addTagMutation.mutate(name)}
                onCancel={() => setShowAddTag(false)}
              />
            )}
          </div>


          {/* ── Business assignment (primary line) — always visible ──────── */}
          {businesses.length > 0 && (
            <div style={{ marginTop: '1.25rem', paddingTop: '0.6rem', borderTop: '1px dashed var(--pb-border)' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--pb-muted)', marginBottom: '0.5rem' }}>
                Business
              </div>
              <BusinessToggle
                businessId={primaryLineBusinessId}
                businesses={businesses}
                onChange={setPrimaryLineBusinessId}
              />
            </div>
          )}

          {/* ── Split rows ─────────────────────────────────────────────── */}
          {!isNew && splitEnabled && (() => {
            const totalAmount    = Math.abs(tx?.charges || 0)
            const originalAmount = Math.abs(parseFloat(form.charges) || 0)
            const allocatedSum   = splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
            const remaining      = Math.round((totalAmount - originalAmount - allocatedSum) * 100) / 100
            const isOver         = remaining < -0.005
            const fmtAmt = (n: number) => Math.abs(n).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })

            const updateRow = (key: number, patch: Partial<SplitRow>) =>
              setSplitRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r))
            const removeRow = (key: number) =>
              setSplitRows(prev => prev.length > 1 ? prev.filter(r => r.key !== key) : prev)
            const fillRemaining = (key: number) => {
              const other = splitRows.filter(r => r.key !== key).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
              const fill  = Math.round((totalAmount - originalAmount - other) * 100) / 100
              if (fill > 0.005) updateRow(key, { amount: String(fill) })
            }

            return (
              <div style={{ marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px dashed var(--pb-border)' }}>
                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--pb-muted)' }}>
                    Split allocation
                  </span>
                  <span style={{ padding: '0.08rem 0.5rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)' }}>
                    Total: {fmtAmt(totalAmount)}
                  </span>
                </div>

                {/* Split rows */}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {splitRows.map((row, idx) => {
                    const rowSubCats = (() => {
                      if (!row.categoryId) return []
                      const cat = settings?.categories.find(c => c.id === Number(row.categoryId))
                      return cat?.subCategories ?? []
                    })()
                    const rowTagIds = new Set(row.tagIds)

                    return (
                      <div key={row.key} style={{
                        padding: '0.85rem 1rem', borderRadius: 12,
                        background: 'var(--pb-glass-bg)',
                        border: '1px solid var(--pb-glass-border)',
                      }}>
                        {/* Row header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                          <span style={{
                            minWidth: 20, height: 20, borderRadius: 999, fontSize: '0.68rem', fontWeight: 700,
                            background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}>{idx + 1}</span>
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--pb-muted)' }}>Split {idx + 1}</span>
                          <button type="button" onClick={() => removeRow(row.key)} style={{
                            marginLeft: 'auto', background: 'none', border: 'none',
                            cursor: splitRows.length > 1 ? 'pointer' : 'default',
                            color: splitRows.length > 1 ? 'var(--pb-muted)' : 'rgba(148,163,184,0.25)',
                            fontSize: '0.82rem', padding: '0.1rem 0.25rem',
                          }}><i className="bi bi-trash3" /></button>
                        </div>

                        {/* Personal / Business toggle */}
                        <div style={{ marginBottom: '0.65rem' }}>
                          <BusinessToggle
                            businessId={row.businessId}
                            businesses={businesses}
                            onChange={val => updateRow(row.key, { businessId: val })}
                          />
                        </div>

                        <div className="row g-2">
                          {/* Amount */}
                          <div className="col-sm-4">
                            <label style={{ ...FIELD_LABEL, marginBottom: 3 }}>Amount</label>
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                              <input type="number" min="0.01" step="0.01" placeholder="0.00"
                                value={row.amount}
                                onChange={e => updateRow(row.key, { amount: e.target.value })}
                                style={{ ...INPUT, fontSize: '0.9rem', padding: '0.55rem 0.75rem' }}
                              />
                              {remaining > 0.01 && (
                                <button type="button" title="Fill remaining" onClick={() => fillRemaining(row.key)} style={{
                                  flexShrink: 0, padding: '0.55rem 0.5rem', borderRadius: 8,
                                  border: '1px solid rgba(99,102,241,0.25)',
                                  background: 'rgba(99,102,241,0.07)', color: 'var(--pb-primary)',
                                  cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                                }}>+{fmtAmt(remaining)}</button>
                              )}
                            </div>
                          </div>
                          {/* Category */}
                          <div className="col-sm-4">
                            <label style={{ ...FIELD_LABEL, marginBottom: 3 }}>Category</label>
                            <div style={{ position: 'relative' }}>
                              <select value={row.categoryId}
                                onChange={e => {
                                  if (e.target.value === '__new__') { e.target.value = row.categoryId; setSplitAddCatKey(row.key) }
                                  else updateRow(row.key, { categoryId: e.target.value, subCategoryId: '' })
                                }}
                                style={{ ...SELECT, fontSize: '0.9rem', padding: '0.55rem 2.2rem 0.55rem 0.75rem' }}>
                                <option value="">— none —</option>
                                {settings?.categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                <option value="__new__" style={{ color: '#6366f1', fontWeight: 600 }}>＋ Add new…</option>
                              </select>
                              <i className="bi bi-chevron-down" style={{ position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.75rem', color: 'var(--pb-muted)' }} />
                            </div>
                            {splitAddCatKey === row.key && (
                              <InlineAdd placeholder="New category name…" icon="bi-tags"
                                loading={addSplitCatMutation.isPending}
                                onConfirm={name => addSplitCatMutation.mutate({ name, rowKey: row.key })}
                                onCancel={() => setSplitAddCatKey(null)} />
                            )}
                          </div>
                          {/* Subcategory */}
                          <div className="col-sm-4">
                            <label style={{ ...FIELD_LABEL, marginBottom: 3 }}>Subcategory</label>
                            <div style={{ position: 'relative' }}>
                              <select value={row.subCategoryId}
                                onChange={e => {
                                  if (e.target.value === '__new__') { e.target.value = row.subCategoryId; setSplitAddSubKey(row.key) }
                                  else updateRow(row.key, { subCategoryId: e.target.value })
                                }}
                                disabled={!row.categoryId}
                                style={{ ...SELECT, fontSize: '0.9rem', padding: '0.55rem 2.2rem 0.55rem 0.75rem', opacity: row.categoryId ? 1 : 0.5, cursor: row.categoryId ? 'pointer' : 'default' }}>
                                <option value="">— none —</option>
                                {rowSubCats.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                {row.categoryId && <option value="__new__" style={{ color: '#6366f1', fontWeight: 600 }}>＋ Add new…</option>}
                              </select>
                              <i className="bi bi-chevron-down" style={{ position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.75rem', color: 'var(--pb-muted)', opacity: row.categoryId ? 1 : 0.4 }} />
                            </div>
                            {splitAddSubKey === row.key && (
                              <InlineAdd placeholder="New subcategory name…" icon="bi-tag"
                                loading={addSplitSubMutation.isPending}
                                onConfirm={name => addSplitSubMutation.mutate({ name, rowCatId: Number(row.categoryId), rowKey: row.key })}
                                onCancel={() => setSplitAddSubKey(null)} />
                            )}
                          </div>
                          {/* Tags */}
                          <div className="col-12">
                            <label style={{ ...FIELD_LABEL, marginBottom: 5 }}>Tags</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
                              {settings?.tags.map(t => {
                                const active = rowTagIds.has(t.id)
                                return (
                                  <button key={t.id} type="button"
                                    onClick={() => {
                                      const next = new Set(rowTagIds)
                                      active ? next.delete(t.id) : next.add(t.id)
                                      updateRow(row.key, { tagIds: Array.from(next) })
                                    }}
                                    style={{
                                      padding: '0.18rem 0.55rem', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600,
                                      border: `1.5px solid ${active ? 'transparent' : 'var(--pb-border)'}`,
                                      background: active ? (t.color ?? '#4f46e5') : 'transparent',
                                      color: active ? '#fff' : 'var(--pb-muted)',
                                      cursor: 'pointer', transition: 'all 0.1s',
                                    }}>
                                    {t.name}
                                  </button>
                                )
                              })}
                              {splitAddTagKey !== row.key && (
                                <button type="button" onClick={() => setSplitAddTagKey(row.key)} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                  padding: '0.18rem 0.5rem', borderRadius: 999,
                                  fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                                  border: '1.5px dashed #a5b4fc', background: 'transparent', color: '#6366f1',
                                  transition: 'border-color 0.12s',
                                }}>
                                  <i className="bi bi-plus" style={{ fontSize: '0.85rem' }} /> New tag
                                </button>
                              )}
                            </div>
                            {splitAddTagKey === row.key && (
                              <InlineAdd placeholder="New tag name…" icon="bi-tag"
                                loading={addSplitTagMutation.isPending}
                                onConfirm={name => addSplitTagMutation.mutate({ name, rowKey: row.key })}
                                onCancel={() => setSplitAddTagKey(null)} />
                            )}
                          </div>
                          {/* Note */}
                          <div className="col-12">
                            <label style={{ ...FIELD_LABEL, marginBottom: 3 }}>Note <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <input type="text" placeholder="e.g. birthday gift"
                              value={row.note}
                              onChange={e => updateRow(row.key, { note: e.target.value })}
                              style={{ ...INPUT, fontSize: '0.9rem', padding: '0.55rem 0.75rem' }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Add row button */}
                <button type="button" onClick={() => setSplitRows(prev => [...prev, blankRow()])} style={{
                  marginTop: '0.45rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.35rem 0.7rem', borderRadius: 8,
                  border: '1px dashed rgba(99,102,241,0.35)', background: 'transparent',
                  color: 'var(--pb-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                }}>
                  <i className="bi bi-plus-circle" /> Add split
                </button>

                {/* Balance pill */}
                <div style={{
                  marginTop: '0.75rem', padding: '0.5rem 0.85rem', borderRadius: 9, display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                  background: isOver ? 'rgba(239,68,68,0.07)' : 'rgba(20,147,107,0.08)',
                  border: `1px solid ${isOver ? 'rgba(239,68,68,0.25)' : 'rgba(20,147,107,0.25)'}`,
                  fontSize: '0.82rem', fontWeight: 600,
                  color: isOver ? '#ef4444' : 'var(--pb-success)',
                }}>
                  <i className={`bi ${isOver ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'}`} />
                  {isOver
                    ? `Over by ${fmtAmt(Math.abs(remaining))}`
                    : remaining < 0.005
                      ? `Fully split — original gets $0.00`
                      : `${fmtAmt(remaining)} remaining on original`
                  }
                </div>
              </div>
            )
          })()}

          {/* Bulk Apply */}
          {hasBulkApply && (
            <>
              <div style={{
                fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.14em', color: 'var(--pb-muted)',
                margin: '1.25rem 0 0.6rem', paddingTop: '0.6rem',
                borderTop: '1px dashed var(--pb-border)',
              }}>
                Bulk apply
              </div>

              <div style={{
                borderRadius: 14,
                border: `1px solid ${applyToOthers ? 'rgba(99,102,241,0.32)' : 'rgba(148,163,184,0.35)'}`,
                background: applyToOthers ? 'rgba(99,102,241,0.03)' : 'rgba(148,163,184,0.06)',
                overflow: 'hidden', transition: 'border-color 0.2s, background 0.2s',
              }}>
                {/* Toggle header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.8rem 1rem' }}>
                  <div role="switch" aria-checked={applyToOthers} onClick={() => setApplyToOthers(v => !v)} style={{
                    position: 'relative', width: 38, height: 21, flexShrink: 0, borderRadius: 999, cursor: 'pointer',
                    background: applyToOthers ? '#6366f1' : 'rgba(148,163,184,0.45)', transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 2.5, left: applyToOthers ? 19.5 : 2.5,
                      width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left 0.2s',
                    }} />
                  </div>
                  <label onClick={() => setApplyToOthers(v => !v)} style={{
                    fontSize: '0.88rem', fontWeight: 600, color: 'var(--pb-text)', flex: 1, cursor: 'pointer',
                  }}>
                    Apply to other transactions
                  </label>
                </div>

                {applyToOthers && (
                  <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid rgba(148,163,184,0.35)' }}>

                    {/* Match by */}
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--pb-muted)', marginTop: '0.75rem' }}>
                      Match by
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', margin: '0.5rem 0 1rem' }}>
                      {sameMerchantCount > 0 && (
                        <button type="button" onClick={() => setApplyMatchBy('merchant')} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.65rem',
                          padding: '0.32rem 0.8rem', borderRadius: 8,
                          border: `1px solid ${applyMatchBy === 'merchant' ? 'rgba(99,102,241,0.3)' : 'rgba(148,163,184,0.35)'}`,
                          background: applyMatchBy === 'merchant' ? 'rgba(99,102,241,0.08)' : 'transparent',
                          fontSize: '0.8rem', fontWeight: applyMatchBy === 'merchant' ? 600 : 500,
                          color: applyMatchBy === 'merchant' ? 'var(--pb-primary)' : 'var(--pb-muted)',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                          <i className="bi bi-shop" /> Merchant name
                          <span onClick={e => { e.stopPropagation(); openModal('all') }} style={{
                            fontSize: '0.68rem', borderRadius: 999, padding: '0.05rem 0.4rem',
                            background: 'rgba(99,102,241,0.12)', color: 'var(--pb-primary)', cursor: 'pointer',
                          }}>
                            {relatedFetching ? '...' : sameMerchantCount}
                          </span>
                        </button>
                      )}
                      {sameDescriptionCount > 0 && (
                        <button type="button" onClick={() => setApplyMatchBy('description')} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.65rem',
                          padding: '0.32rem 0.8rem', borderRadius: 8,
                          border: `1px solid ${applyMatchBy === 'description' ? 'rgba(99,102,241,0.3)' : 'rgba(148,163,184,0.35)'}`,
                          background: applyMatchBy === 'description' ? 'rgba(99,102,241,0.08)' : 'transparent',
                          fontSize: '0.8rem', fontWeight: applyMatchBy === 'description' ? 600 : 500,
                          color: applyMatchBy === 'description' ? 'var(--pb-primary)' : 'var(--pb-muted)',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                          <i className="bi bi-file-text" /> Original description
                          <span onClick={e => { e.stopPropagation(); openModal('all') }} style={{
                            fontSize: '0.68rem', borderRadius: 999, padding: '0.05rem 0.4rem',
                            background: 'rgba(99,102,241,0.12)', color: 'var(--pb-primary)', cursor: 'pointer',
                          }}>
                            {relatedFetching ? '...' : sameDescriptionCount}
                          </span>
                        </button>
                      )}
                    </div>

                    {/* What to apply label */}
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--pb-muted)', marginBottom: '0.5rem' }}>
                      What to apply
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>

                      {/* Category */}
                      <div style={{ padding: '0.65rem 0.25rem', background: applyCategory ? 'rgba(99,102,241,0.04)' : 'transparent', transition: 'background 0.15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                            <input type="checkbox" style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }}
                              checked={applyCategory} onChange={e => setApplyCategory(e.target.checked)} />
                            <span style={{ fontSize: '0.82rem', fontWeight: applyCategory ? 500 : 400, color: applyCategory ? 'var(--pb-text)' : 'var(--pb-muted)', transition: 'color 0.12s' }}>
                              Category / Subcategory
                            </span>
                          </label>
                          <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto' }}>
                            {(['all', 'unknownOnly'] as const).map(mode => (
                              <button key={mode} type="button" onClick={() => setApplyCategoryMode(mode)} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.2rem 0.6rem', borderRadius: 6,
                                border: `1px solid ${applyCategoryMode === mode ? 'rgba(99,102,241,0.3)' : 'rgba(148,163,184,0.35)'}`,
                                background: applyCategoryMode === mode ? 'rgba(99,102,241,0.08)' : 'transparent',
                                fontSize: '0.73rem', fontWeight: 600,
                                color: applyCategoryMode === mode ? 'var(--pb-primary)' : 'var(--pb-muted)',
                                cursor: 'pointer', transition: 'all 0.12s',
                              }}>
                                {mode === 'all' ? 'All' : 'Unknown only'}
                                <span onClick={e => { e.stopPropagation(); openModal(mode) }} style={{
                                  minWidth: 18, height: 16, borderRadius: 999, padding: '0 0.28rem',
                                  fontSize: '0.65rem', fontWeight: 700,
                                  background: 'rgba(99,102,241,0.12)', color: 'var(--pb-primary)',
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                }}>
                                  {mode === 'all' ? total : noCat}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Tags */}
                      <div style={{ borderTop: '1px solid rgba(148,163,184,0.35)', padding: '0.65rem 0.25rem', background: applyTags ? 'rgba(99,102,241,0.04)' : 'transparent', transition: 'background 0.15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                            <input type="checkbox" style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }}
                              checked={applyTags} onChange={e => setApplyTags(e.target.checked)} />
                            <span style={{ fontSize: '0.82rem', fontWeight: applyTags ? 500 : 400, color: applyTags ? 'var(--pb-text)' : 'var(--pb-muted)', transition: 'color 0.12s' }}>
                              Tags
                            </span>
                          </label>
                          <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto' }}>
                            {(['all', 'noTagsOnly'] as const).map(mode => (
                              <button key={mode} type="button" onClick={() => setApplyTagsMode(mode)} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.2rem 0.6rem', borderRadius: 6,
                                border: `1px solid ${applyTagsMode === mode ? 'rgba(99,102,241,0.3)' : 'rgba(148,163,184,0.35)'}`,
                                background: applyTagsMode === mode ? 'rgba(99,102,241,0.08)' : 'transparent',
                                fontSize: '0.73rem', fontWeight: 600,
                                color: applyTagsMode === mode ? 'var(--pb-primary)' : 'var(--pb-muted)',
                                cursor: 'pointer', transition: 'all 0.12s',
                              }}>
                                {mode === 'all' ? 'All' : 'Untagged only'}
                                <span onClick={e => { e.stopPropagation(); openModal(mode) }} style={{
                                  minWidth: 18, height: 16, borderRadius: 999, padding: '0 0.28rem',
                                  fontSize: '0.65rem', fontWeight: 700,
                                  background: 'rgba(99,102,241,0.12)', color: 'var(--pb-primary)',
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                }}>
                                  {mode === 'all' ? total : noTags}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Merchant rename */}
                      <div style={{ borderTop: '1px solid rgba(148,163,184,0.35)', padding: '0.65rem 0.25rem', background: applyMerchant ? 'rgba(99,102,241,0.04)' : 'transparent', transition: 'background 0.15s' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                          <input type="checkbox" style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }}
                            checked={applyMerchant} onChange={e => setApplyMerchant(e.target.checked)} />
                          <span style={{ fontSize: '0.82rem', fontWeight: applyMerchant ? 500 : 400, color: applyMerchant ? 'var(--pb-text)' : 'var(--pb-muted)', transition: 'color 0.12s' }}>
                            Merchant
                          </span>
                        </label>
                      </div>

                      {/* Business assignment */}
                      {businesses.length > 0 && (
                        <div style={{ borderTop: '1px solid rgba(148,163,184,0.35)', padding: '0.65rem 0.25rem', background: applyBusiness ? 'rgba(99,102,241,0.04)' : 'transparent', transition: 'background 0.15s' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                              <input type="checkbox" style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }}
                                checked={applyBusiness} onChange={e => {
                                  setApplyBusiness(e.target.checked)
                                  // Bulk assignment follows the business chosen on the line above.
                                  if (e.target.checked) setApplyBusinessId(primaryLineBusinessId)
                                }} />
                              <span style={{ fontSize: '0.82rem', fontWeight: applyBusiness ? 500 : 400, color: applyBusiness ? 'var(--pb-text)' : 'var(--pb-muted)', transition: 'color 0.12s' }}>
                                Business assignment
                              </span>
                            </label>
                            {applyBusiness && (
                              <span style={{
                                fontSize: '0.8rem', padding: '0.2rem 0.6rem', borderRadius: 7, marginLeft: 'auto',
                                border: '1px solid var(--pb-border)', background: 'rgba(99,102,241,0.06)',
                                color: 'var(--pb-text)', fontWeight: 600,
                              }}>
                                {businesses.find(b => b.businessID === primaryLineBusinessId)?.businessName ?? 'Personal'}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Preview button — count updates based on checked options */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px dashed rgba(148,163,184,0.35)' }}>
                      <button type="button"
                        disabled={effectiveCount === 0 && !relatedFetching}
                        onClick={() => openModal(applyCategory || applyTags || applyMerchant ? 'effective' : 'all')}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                          padding: '0.32rem 0.55rem 0.32rem 0.75rem', borderRadius: 999,
                          border: '1px solid rgba(148,163,184,0.35)', background: 'transparent',
                          color: 'var(--pb-muted)', fontSize: '0.8rem', fontWeight: 600,
                          cursor: effectiveCount === 0 && !relatedFetching ? 'default' : 'pointer',
                          opacity: effectiveCount === 0 && !relatedFetching ? 0.4 : 1, transition: 'all 0.15s',
                        }}>
                        <i className="bi bi-eye" style={{ fontSize: '0.75rem', opacity: 0.7 }} />
                        Preview
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 22, height: 22, padding: '0 0.32rem', borderRadius: 999,
                          background: effectiveCount === 0 && !relatedFetching ? 'rgba(148,163,184,0.35)' : '#6366f1',
                          color: effectiveCount === 0 && !relatedFetching ? 'var(--pb-muted)' : '#fff',
                          fontSize: '0.75rem', fontWeight: 700,
                        }}>
                          {relatedFetching ? '...' : effectiveCount}
                        </span>
                      </button>
                      <span style={{ fontSize: '0.78rem', color: 'var(--pb-muted)' }}>
                        transactions will be affected
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button type="button" className="pb-btn-ghost"
              style={{ color: 'var(--pb-muted)', fontWeight: 500, padding: '0.6rem 0.9rem', borderRadius: 999 }}
              onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left me-1" /> Back to list
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
              <button type="button" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()} style={{
                background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)',
                color: '#fff', fontWeight: 600, border: 'none',
                padding: '0.7rem 1.4rem', borderRadius: 999,
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                boxShadow: '0 12px 24px -12px rgba(99,102,241,0.6)',
                cursor: saveMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: saveMutation.isPending ? 0.8 : 1,
              }}>
                {saveMutation.isPending
                  ? <><span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} /> Saving...</>
                  : <><i className="bi bi-check2-circle" /> {isNew ? 'Create Transaction' : 'Save Changes'}</>
                }
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Related Transactions Modal — centered below navbar (~60px) */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setShowModal(false)} />
          <div
            className="modal fade show"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              paddingTop: 60, paddingBottom: 24, zIndex: 1050,
            }}
            onClick={e => { if (e.target === e.currentTarget) setShowModal(false) }}
          >
            <div className="modal-dialog modal-xl" style={{
              margin: 0, width: '95%', maxWidth: 1140,
              maxHeight: 'calc(100vh - 60px - 48px)',
              display: 'flex', flexDirection: 'column',
            }}>
              <div className="modal-content" style={{
                background: 'var(--pb-glass-bg-strong)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                color: 'var(--pb-text)', border: '1px solid var(--pb-border)',
                boxShadow: 'var(--pb-glass-shadow)',
                borderRadius: 16, display: 'flex', flexDirection: 'column',
                overflow: 'hidden', maxHeight: 'calc(100vh - 60px - 48px)',
              }}>
                <div className="modal-header" style={{ borderBottom: '1px solid var(--pb-border)', background: 'transparent', borderRadius: '16px 16px 0 0', flexShrink: 0 }}>
                  <h5 className="modal-title" style={{ fontWeight: 700, fontSize: '1rem' }}>
                    <i className="bi bi-layers me-2 text-muted" />
                    Matching transactions
                    {filterLabel && <small style={{ color: 'var(--pb-muted)', fontWeight: 400 }}>{filterLabel}</small>}
                  </h5>
                  <button className="btn-close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body" style={{ padding: 0, overflowY: 'auto', flex: '1 1 auto' }}>
                  {relatedFetching && (
                    <div className="d-flex align-items-center justify-content-center" style={{ padding: '3rem' }}>
                      <span className="spinner-border spinner-border-sm text-primary me-2" />
                      <span style={{ color: 'var(--pb-muted)' }}>Loading...</span>
                    </div>
                  )}
                  {!relatedFetching && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          {['Date', 'Merchant', 'Category / Subcategory', 'Tags', 'Amount'].map((h, i) => (
                            <th key={h} style={{
                              fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '0.07em', color: 'var(--pb-muted)',
                              padding: '0.5rem 0.75rem', textAlign: i === 4 ? 'right' : 'left',
                              borderBottom: '2px solid var(--pb-border)', background: 'var(--pb-glass-bg-strong)',
                              position: 'sticky', top: 0, zIndex: 1,
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length === 0 && (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--pb-muted)', fontStyle: 'italic' }}>
                              No matching transactions
                            </td>
                          </tr>
                        )}
                        {filteredRows.map((r, i) => {
                          const isSplit = !!(r.isSplit && r.splitLines && r.splitLines.length > 0)
                          return (
                          <tr key={r.id ?? i} style={{ borderBottom: '1px solid var(--pb-border)' }}
                            onMouseEnter={e => Array.from(e.currentTarget.children).forEach((td: Element) => ((td as HTMLElement).style.background = 'rgba(99,102,241,0.04)'))}
                            onMouseLeave={e => Array.from(e.currentTarget.children).forEach((td: Element) => ((td as HTMLElement).style.background = ''))}>
                            <td style={{ padding: '0.55rem 0.75rem', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{r.date}</td>
                            <td style={{ padding: '0.55rem 0.75rem', verticalAlign: 'top' }}>{r.merchantDisplayName}</td>
                            {/* Category — P/B + colored pill; stacked per line for splits */}
                            <td style={{ padding: isSplit ? '0.55rem 0 0.55rem 0.75rem' : '0.55rem 0.75rem', verticalAlign: 'top' }}>
                              {isSplit ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                  {r.splitLines!.map((line, li) => (
                                    <div key={li} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(148,163,184,0.08)', border: '1px solid var(--pb-border)', borderRight: 'none', borderRadius: '6px 0 0 6px', padding: '0.28rem 0.5rem', minHeight: '1.65rem' }}>
                                      <PBBadge businessId={line.businessId} />
                                      <CatPill category={line.category} subCategory={line.subCategory} />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                  <PBBadge businessId={r.primaryLineBusinessId} />
                                  <CatPill category={r.category} subCategory={r.subCategory} />
                                </span>
                              )}
                            </td>
                            {/* Tags — stacked per line (with amount) for splits */}
                            <td style={{ padding: isSplit ? '0.55rem 0.75rem 0.55rem 0' : '0.55rem 0.75rem', verticalAlign: 'top' }}>
                              {isSplit ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                  {r.splitLines!.map((line, li) => (
                                    <div key={li} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'rgba(148,163,184,0.08)', border: '1px solid var(--pb-border)', borderLeft: 'none', borderRadius: '0 6px 6px 0', padding: '0.28rem 0.5rem', minHeight: '1.65rem' }}>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', flex: 1 }}>
                                        {line.tags && line.tags.length > 0
                                          ? line.tags.map(tg => <span key={tg} style={{ display: 'inline-block', fontSize: '0.64rem', fontWeight: 600, padding: '0.05rem 0.3rem', borderRadius: 4, background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)', whiteSpace: 'nowrap' }}>{tg}</span>)
                                          : <span style={{ color: 'var(--pb-muted)', fontSize: '0.72rem' }}>—</span>}
                                      </div>
                                      <span style={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{money(line.amount)}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                r.hasTags ? r.tags : <span style={{ color: 'var(--pb-muted)', fontStyle: 'italic', fontSize: '0.8rem' }}><i className="bi bi-tag" style={{ fontSize: '0.7rem' }} /> None</span>
                              )}
                            </td>
                            <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 600, verticalAlign: 'top' }}>
                              {money(r.amount)}
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid var(--pb-border)', background: 'transparent', borderRadius: '0 0 16px 16px', fontSize: '0.8rem', color: 'var(--pb-muted)', flexShrink: 0 }}>
                  <span>{filteredRows.length} transaction{filteredRows.length !== 1 ? 's' : ''} &middot; newest first</span>
                  <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => setShowModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
